// frontend/src/pages/Pilots.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import {
    PlusIcon,
    RocketLaunchIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';

const Pilots = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [pilots, setPilots] = useState([]);
    const [stats, setStats] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [showCreateForm, setShowCreateForm] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [pilotsRes, statsRes, templatesRes] = await Promise.all([
                axios.get('/pilots/my-pilots'),
                axios.get('/pilots/statistics'),
                axios.get('/pilots/templates')
            ]);
            setPilots(pilotsRes.data.data);
            setStats(statsRes.data.data);
            setTemplates(templatesRes.data.data);
        } catch (error) {
            toast.error('Failed to load pilot data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-600">Loading pilots...</div>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6">
            {/* Header */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex justify-between items-center flex-wrap gap-4">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Pilot Projects</h1>
                        <p className="text-gray-600 mt-1">
                            Manage your AI solution pilots and track implementation
                        </p>
                    </div>
                    <button
                        onClick={() => setShowCreateForm(!showCreateForm)}
                        className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors flex items-center space-x-2 text-sm font-medium"
                    >
                        <PlusIcon className="w-5 h-5" />
                        <span>New Pilot</span>
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <p className="text-sm text-gray-500">Total</p>
                            <p className="text-xl font-semibold">{stats.total || 0}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <p className="text-sm text-gray-500">Planned</p>
                            <p className="text-xl font-semibold text-gray-600">{stats.planned || 0}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <p className="text-sm text-gray-500">Active</p>
                            <p className="text-xl font-semibold text-blue-600">{stats.active || 0}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <p className="text-sm text-gray-500">Completed</p>
                            <p className="text-xl font-semibold text-green-600">{stats.completed || 0}</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Create Form */}
            {showCreateForm && (
                <PilotCreateForm 
                    templates={templates}
                    onSuccess={() => {
                        setShowCreateForm(false);
                        loadData();
                    }}
                    onCancel={() => setShowCreateForm(false)}
                />
            )}

            {/* Pilot List */}
            {pilots.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                    <RocketLaunchIcon className="w-12 h-12 mx-auto text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No pilots yet</h3>
                    <p className="text-sm text-gray-500 mt-1">
                        Start a pilot to implement a solution with a business
                    </p>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="mt-3 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
                    >
                        Start a Pilot
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pilots.map((pilot) => (
                        <PilotCard key={pilot.id} pilot={pilot} />
                    ))}
                </div>
            )}
        </div>
    );
};

const PilotCard = ({ pilot }) => {
    const progress = pilot.progress || 0;
    const statusColor = {
        planned: 'bg-gray-100 text-gray-800',
        active: 'bg-blue-100 text-blue-800',
        completed: 'bg-green-100 text-green-800',
        cancelled: 'bg-red-100 text-red-800'
    }[pilot.status] || 'bg-gray-100 text-gray-800';

    return (
        <Link to={`/pilots/${pilot.id}`} className="block">
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{pilot.name}</h3>
                        {pilot.business && (
                            <p className="text-sm text-gray-600">🏢 {pilot.business.name}</p>
                        )}
                        {pilot.solution && (
                            <p className="text-sm text-gray-500">💡 {pilot.solution.name}</p>
                        )}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${statusColor}`}>
                        {pilot.status}
                    </span>
                </div>

                <div className="mt-3 flex items-center space-x-4 text-sm text-gray-500">
                    <span>📅 {new Date(pilot.start_date).toLocaleDateString()}</span>
                    {pilot.end_date && (
                        <span>→ {new Date(pilot.end_date).toLocaleDateString()}</span>
                    )}
                    <span>•</span>
                    <span>{pilot.milestones?.length || 0} milestones</span>
                </div>

                <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-500">
                        <span>Progress</span>
                        <span>{progress}%</span>
                    </div>
                    <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                            className="h-full bg-black transition-all duration-500"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>

                <div className="mt-3 flex justify-end">
                    <span className="text-sm text-black font-medium flex items-center">
                        View Details <ArrowRightIcon className="w-4 h-4 ml-1" />
                    </span>
                </div>
            </div>
        </Link>
    );
};

const PilotCreateForm = ({ templates, onSuccess, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [businesses, setBusinesses] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        solution_match_id: '',
        business_id: '',
        start_date: '',
        end_date: '',
        template_id: '',
        objectives: '',
        success_criteria: ''
    });

    useEffect(() => {
        const loadBusinesses = async () => {
            try {
                const response = await axios.get('/business/admin/all');
                setBusinesses(response.data.data || []);
            } catch (error) {
                toast.error('Failed to load businesses');
            }
        };
        loadBusinesses();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = { ...formData };
            if (!String(payload.objectives || '').trim()) delete payload.objectives;
            if (!String(payload.success_criteria || '').trim()) delete payload.success_criteria;
            if (!String(payload.solution_match_id || '').trim()) delete payload.solution_match_id;
            if (!String(payload.business_id || '').trim()) delete payload.business_id;
            else payload.business_id = Number(payload.business_id);
            if (!String(payload.template_id || '').trim()) delete payload.template_id;
            if (!String(payload.description || '').trim()) delete payload.description;

            await axios.post('/pilots', payload);
            toast.success('Pilot created successfully');
            onSuccess();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to create pilot');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Start New Pilot</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        Pilot Name *
                    </label>
                    <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm"
                        placeholder="e.g., AI Chatbot Implementation Pilot"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        Description
                    </label>
                    <textarea
                        rows={2}
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm"
                        placeholder="Describe the pilot project..."
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Start Date *
                        </label>
                        <input
                            type="date"
                            required
                            value={formData.start_date}
                            onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            End Date
                        </label>
                        <input
                            type="date"
                            value={formData.end_date}
                            onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Business *
                        </label>
                        <select
                            required
                            value={formData.business_id}
                            onChange={(e) => setFormData({...formData, business_id: e.target.value})}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm bg-white"
                        >
                            <option value="">Select a business</option>
                            {businesses.map((business) => (
                                <option key={business.id} value={business.id}>
                                    {business.name}
                                    {!business.assigned_intern_id ? ' (no intern assigned)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Template (Optional)
                        </label>
                        <select
                            value={formData.template_id}
                            onChange={(e) => setFormData({...formData, template_id: e.target.value})}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm bg-white"
                        >
                            <option value="">None</option>
                            {templates.map((template) => (
                                <option key={template.id} value={template.id}>
                                    {template.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Solution Match ID (Optional)
                        </label>
                        <input
                            type="text"
                            value={formData.solution_match_id}
                            onChange={(e) => setFormData({...formData, solution_match_id: e.target.value})}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm"
                            placeholder="Enter solution match ID"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        Objectives
                    </label>
                    <textarea
                        rows={2}
                        value={formData.objectives}
                        onChange={(e) => setFormData({...formData, objectives: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm"
                        placeholder="What are the main objectives of this pilot?"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        Success Criteria
                    </label>
                    <textarea
                        rows={2}
                        value={formData.success_criteria}
                        onChange={(e) => setFormData({...formData, success_criteria: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm"
                        placeholder="How will you measure success?"
                    />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md transition-colors text-sm font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading || !formData.name || !formData.start_date || !formData.business_id}
                        className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 text-sm font-medium"
                    >
                        {loading ? 'Creating...' : 'Create Pilot'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Pilots;
